import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import notificationService from '../services/notification.service'
import { useAuth } from '../hooks/useAuth.jsx'

const NotificationContext = createContext();

export const useNotifications = () => {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error('useNotifications must be used within a NotificationProvider');
	}
	return context;
};

export const NotificationProvider = ({ children }) => {
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [loading, setLoading] = useState(false);
	const [streamStatus, setStreamStatus] = useState('disconnected');

	const { user, isLoading: authLoading } = useAuth()
	const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
	const isAuthenticated = !!token && !!user

	const fetchNotifications = useCallback(async (params = {}) => {
		if (!isAuthenticated) return

		try {
			setLoading(true);
			const data = await notificationService.getUserNotifications(params);
			setNotifications(data.notifications);
			setUnreadCount(data.unreadCount);
			return data;
		} catch (error) {
			console.error('Failed to fetch notifications', error);
			return null;
		} finally {
			setLoading(false);
		}
	}, [isAuthenticated])

	const markAsRead = async (id) => {
		try {
			// Optimistic update
			setNotifications((prev) =>
				prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
			)
			setUnreadCount((prev) => Math.max(0, prev - 1))

			await notificationService.markAsRead(id);
		} catch (error) {
			console.error('Failed to mark as read', error);
			// Revert on error? Or just refresh
			fetchNotifications();
		}
	};

	const markAllAsRead = async () => {
		try {
			// Optimistic update
			setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
			setUnreadCount(0)

			await notificationService.markAllAsRead();
		} catch (error) {
			console.error('Failed to mark all as read', error);
			fetchNotifications();
		}
	};

	// Initial fetch
	useEffect(() => {
		if (authLoading) return
		if (isAuthenticated) {
			fetchNotifications();
		} else {
			setNotifications([])
			setUnreadCount(0)
		}
	}, [fetchNotifications, authLoading, isAuthenticated]);

	useEffect(() => {
		if (!isAuthenticated) return
		const token = localStorage.getItem('token');
		if (!token) return;

		setStreamStatus('connecting');
		const stream = notificationService.connectStream({
			token,
			onEvent: (eventName, event) => {
				try {
					const payload = JSON.parse(event.data || '{}');
					if (eventName === 'notification:new') {
						const incoming = payload.notification;
						if (incoming) {
							setNotifications((prev) => [incoming, ...prev.filter((n) => n.id !== incoming.id)].slice(0, 50));
							setUnreadCount((prev) => prev + (incoming.isRead ? 0 : 1));
						}
					}

					if (eventName === 'notification:unread_count' || eventName === 'notification:connected') {
						if (typeof payload.unreadCount === 'number') {
							setUnreadCount(payload.unreadCount);
						}
					}

					if (eventName === 'notification:refresh') {
						fetchNotifications();
					}
				} catch (error) {
					console.error('Failed to parse notification stream event', error);
				}
			},
			onError: () => {
				setStreamStatus('error');
			},
		});

		stream.onopen = () => {
			setStreamStatus('connected');
		};

		return () => {
			setStreamStatus('disconnected');
			stream.close();
		};
	}, [fetchNotifications, isAuthenticated]);

	const value = {
		notifications,
		unreadCount,
		loading,
		streamStatus,
		fetchNotifications,
		markAsRead,
		markAllAsRead
	};

	return (
		<NotificationContext.Provider value={value}>
			{children}
		</NotificationContext.Provider>
	);
};
